<script setup lang="ts">
// Script content remains the same as your working version
import { computed, inject, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import colors from '@/colors.json';
import { IonSpinner, useIonRouter } from '@ionic/vue';
import BRadioItem from '@/components/BRadioItem.vue';
import BIconButton from '@/components/BIconButton.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import { useStore } from '@/stores/main';
import { useBackendFetch } from '@/fetch';
import monsterUrl from '/assets/monster/arg.png';

const props = defineProps({
  data: Object,
});

const store = useStore();
const router = useIonRouter();

const colorScheme = inject('colorScheme', 'yellow');
const doNextSlide = inject('nextSlide');
const previousSlide = inject('previousSlide');

const loading = ref(false);

const warningOpen = ref(false);
const questionAnswered = ref(false);
const isVisible = ref(true); // Track if this slide is visible

// Listen for visibility changes
onMounted(() => {
  isVisible.value = true;
  
  // Reset warning state when component is mounted
  warningOpen.value = false;
});

function nextSlide() {
  // First check if we need to show a warning
  let shouldShowWarning = false;
  const factor = store.profile?.factors?.[props.data?.category_id];
  
  // Check if question is answered
  if (!factor || factor.value === undefined || factor.value === null) {
    // No factor or no value means question not answered
    questionAnswered.value = false;
    shouldShowWarning = true;
  } else if (factor.status === 'profile.unknown' || factor.status === 'unknown') {
    // Status indicates unknown/unanswered
    questionAnswered.value = false;
    shouldShowWarning = true;
  } else {
    // Question is answered
    questionAnswered.value = true;
    
    // Check if improve selection is made
    if (factor.improve === undefined || factor.improve === null) {
      shouldShowWarning = true;
    }
  }

  if (shouldShowWarning) {
    warningOpen.value = true;
    return;
  }

  // If no warning needed, proceed to next slide
  doNextSlide();
}

function ignoreWarning() {
  warningOpen.value = false;
  doNextSlide();
}

const getProfileValue = data => {
  return {
    label: store.getProfileFactor(data.category_id)?.label ?? '',
    value: store.getProfileValue(data.name) ?? null,
  };
};
const selectedOption = ref(getProfileValue(props.data));

const backgroundColor = computed(() => {
  return {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  }[colorScheme];
});

const borderColor = computed(() => {
  return {
    purple: 'border-purple-light',
    yellow: 'border-yellow-light',
    green: 'border-green-light',
    blue: 'border-blue-light',
  }[colorScheme];
});

const radioColor = computed(() => {
  return {
    purple: colors.purple.darker,
    yellow: colors.yellow.darker,
    green: colors.green.darker,
    blue: colors.blue.darker,
  }[colorScheme];
});

async function sendProfileValue(name, value) {
  // Optimistic update - update UI immediately, don't wait for API
  selectedOption.value = { label: store.getProfileFactor(props.data.category_id)?.label ?? '', value };
  store.setProfileValue(name, value).then(result => {
    if (result === null) {
      router.replace('/login');
    }
  });
}

async function sendImproveValue(value) {
  // Optimistic update - update UI immediately, don't wait for API
  store.setImproveValue(props.data?.category_id, value).then(result => {
    if (result === null) {
      router.replace('/login');
    }
  });
}

const questionArea = ref(null);
const navigationArea = ref(null);
const pageArea = ref(null);

const scrollButtonVisible = ref(false);

// Modified resize observer logic to be more reliable across devices
const updateScrollButtonVisibility = () => {
  if (!questionArea.value || !navigationArea.value || !pageArea.value) return;
  
  const pageHeight = pageArea.value.clientHeight;
  const navHeight = navigationArea.value.clientHeight;
  const areaHeight = questionArea.value.scrollHeight;
  const availableSpace = pageHeight - navHeight;
  
  scrollButtonVisible.value = areaHeight > availableSpace;
};

const resizeObserver = new ResizeObserver(() => {
  updateScrollButtonVisibility();
});

watch(questionArea, el => {
  if (el === null) {
    return;
  }
  
  resizeObserver.observe(el);
  // Force an initial check
  setTimeout(updateScrollButtonVisibility, 100);
});

onUnmounted(() => {
  if (questionArea.value) {
    resizeObserver.unobserve(questionArea.value);
  }
});

function scroll() {
  if (questionArea.value === null) {
    return;
  }

  questionArea.value.scrollTop += 300;
}

provide('questionData', props.data);

// Shared state for optimistic answer selection - allows radio items to know when another is being selected
const optimisticSelectedValue = ref<any>(null);
provide('optimisticSelectedValue', optimisticSelectedValue);
</script>

<template>
  <!-- Teleport the warning dialog to body to escape swiper stacking context -->
  <Teleport to="body">
    <!-- Dialog -->
    <div 
      v-if="warningOpen"
      class="fixed inset-0 z-[99999] flex items-center justify-center px-4 pointer-events-none"
    >
      <div 
        class="bg-yellow-light border-none rounded-md shadow-2xl p-4 w-full max-w-sm pointer-events-auto"
        @click.stop
      >
        <div class="flex flex-col items-center">
          <img
            :src="monsterUrl"
            alt=""
            class="w-1/3 max-w-[120px] mx-auto"
          />

          <p class="text-base leading-relaxed my-4 text-center" v-if="!questionAnswered">
            Stopp nu! Du har inte svarat på frågan.
          </p>
          <p class="text-base leading-relaxed my-4 text-center" v-else>
            Stopp nu! Du har inte svarat om du är nöjd eller vill förbättra.
          </p>
          <div class="flex gap-x-4 justify-center mt-2">
            <b-button @click="warningOpen = false" look="secondary">Stäng</b-button>
            <b-button @click="ignoreWarning">Gå vidare ändå</b-button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <div
    :class="backgroundColor"
    class="w-full h-full flex flex-col"
    ref="pageArea"
  >
    <div class="overflow-y-scroll scroll-smooth" ref="questionArea">
      <div class="question-header">
        <div class="question-label-container">
          <h2
            class="question-label"
            v-text="data.label"
          ></h2>
        </div>
        <p
          class="question-description"
          v-text="data.description"
        ></p>
      </div>

      <div class="answers-container">
        <ul class="answers-list">
          <b-radio-item
            :color-scheme="colorScheme"
            :name="data.name"
            :data="answer"
            :index="idx"
            v-for="(answer, idx) in data.data"
            @picked="sendProfileValue(data.name, answer.value)"
            @improve="e => sendImproveValue(e)"
            :expand="true"
          />
        </ul>
      </div>
    </div>

    <div
      class="lg:bg-[#FFF3D2] mt-auto px-8 lg:h-[20%] pt-10 pb-8 button-border relative"
      ref="navigationArea"
    >
      <div class="absolute -top-6 right-2" v-if="scrollButtonVisible">
        <b-icon-button @click="scroll"><arrow-down /></b-icon-button>
      </div>

      <div
        class="grid grid-cols-2 gap-4 max-w-md mx-auto lg:max-w-sm"
      >
        <b-button @click="previousSlide" look="secondary">Tillbaka</b-button>
        <b-button @click="nextSlide">Nästa</b-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.button-border {
  box-shadow: 0px -0.5px 2px 0px rgba(0, 0, 0, 0.25);
}

/* Question Header Styling */
.question-header {
  padding: 0 2rem;
  color: #171717;
}

.question-label-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.question-label {
  font-weight: 600;
  font-size: 1.5rem;
  margin: 1.5rem 0 0.5rem 0;
  color: #1a1a1a;
  line-height: 1.3;
}

@media (min-width: 1024px) {
  .question-label {
    font-size: 2rem;
  }
}

.question-description {
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0.5rem 0 1.5rem 0;
  color: #444;
  text-align: start;
}

@media (min-width: 1024px) {
  .question-description {
    font-size: 1.35rem;
  }
}

/* Answers Container */
.answers-container {
  padding: 0 2rem 2rem 2rem;
  flex-grow: 1;
  position: relative;
}

@media (min-width: 1024px) {
  .answers-container {
    width: 50%;
  }
}

.answers-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  gap: 0.75rem;
}
</style>